import { AlertTriangle, Eye, EyeOff } from "@tamagui/lucide-icons";
import { useAtom } from "jotai";
import * as React from "react";
import { useState } from "react";
import { Platform } from "react-native";
import { H6, Paragraph, YStack, XStack, H4, Separator, useTheme } from "tamagui";

import { useScanBarcodes } from "../../../plugins/frameProcessors/js/codeScanner";
import {
  isIgnored,
  type StockLookupResponseRecord,
  useStockLookup,
  type StockFilter,
} from "../../common/api/multiRetail";
import { useHasCamera } from "../../common/camera/camera";
import { Button } from "../../common/components";
import { Alert } from "../../common/components/Alert";
import { FilterSelect } from "../../common/components/FilterSelect";
import { ManagedScanner } from "../../common/components/ManagedScanner";
import { PageContainer, PageScrollNarrow } from "../../common/components/Page";
import { formatPrice } from "../../common/number/number";
import { playHaptic, playSound } from "../../common/sound";
import { productLookupSortAtom } from "../../common/storage/atoms";

interface LookupItem {
  code?: string;
  searchDescription?: string;
}

function WarningIcon() {
  const theme = useTheme();
  return <AlertTriangle size={16} color={theme.color.val} />;
}

function LookupProductItem({
  item,
  setItem,
  setEditItem,
  setIsCameraActive,
  setShowEnterBarcode,
  setShowEnterDescription,
}: {
  item: LookupItem;
  setItem: (value?: LookupItem) => void;
  setEditItem: (value?: LookupItem) => void;
  setIsCameraActive: React.Dispatch<React.SetStateAction<boolean>>;
  setShowEnterBarcode: React.Dispatch<React.SetStateAction<boolean>>;
  setShowEnterDescription: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const [stockFilter, setStockFilter] = React.useState<StockFilter>(
    item.searchDescription ? "excludeNonPositive" : "none"
  );

  const [sortBy, setSortBy] = useAtom(productLookupSortAtom);
  const [checkStockCode, setCheckStockCode] = React.useState(false);
  const query = useStockLookup({
    code: item.code,
    checkStockCode,
    searchDescription: item.searchDescription,
    stockFilter,
  });
  const hasLoaded = query.isFetched;

  const queryResults = React.useMemo(() => {
    const resultsExactMatch =
      query.data?.records.filter((result) => result.Barcode === item.code || String(result.Stock_Code) === item.code) ??
      [];
    const results = resultsExactMatch.length
      ? resultsExactMatch
      : Map.groupBy(
          query.data?.records ?? [],
          (r) => `${r.Stock_Code}|${r.Full_Desc}|${r.BandA_Sell}|${r.Retail_Stock_Balance}`
        )
          .values()
          .map((i) => ({ ...i[0], Barcode: i.map((i) => i.Barcode).join(", ") }))
          .toArray();
    const sorters = {
      default: () => results,
      price_asc: () => results.toSorted((a, b) => Number(a.BandA_Sell) - Number(b.BandA_Sell)),
      price_desc: () => results.toSorted((a, b) => Number(b.BandA_Sell) - Number(a.BandA_Sell)),
      desc_asc: () => results.toSorted((a, b) => a.Full_Desc.localeCompare(b.Full_Desc)),
      desc_desc: () => results.toSorted((a, b) => b.Full_Desc.localeCompare(a.Full_Desc)),
      code_asc: () => results.toSorted((a, b) => a.Stock_Code - b.Stock_Code),
      code_desc: () => results.toSorted((a, b) => b.Stock_Code - a.Stock_Code),
    };
    return (sorters[sortBy] ?? sorters.default)();
  }, [query.data?.records, item.code, sortBy]);

  const [displayAlertUnrecognised, setDisplayAlertUnrecognised] = React.useState<boolean>(false);

  const [showIgnored, setShowIgnored] = useState(false);

  let unignoredResults: StockLookupResponseRecord[];
  let ignoredResults: StockLookupResponseRecord[];
  {
    const actualUnignoredResults = queryResults.filter((i) => !isIgnored(i));
    if (actualUnignoredResults.length === 0) {
      unignoredResults = queryResults;
      ignoredResults = [];
    } else {
      unignoredResults = actualUnignoredResults;
      ignoredResults = queryResults.filter((i) => isIgnored(i));
    }
  }

  React.useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect-jscompat/no-event-handler
    if (hasLoaded) {
      if (queryResults.length) {
        void playSound("high");
        void playHaptic("high");
      } else if (!checkStockCode) {
        // Not valid barcode found, check stock code before declaring it as invalid
        // eslint-disable-next-line react-you-might-not-need-an-effect-jscompat/no-chain-state-updates, react-you-might-not-need-an-effect-jscompat/no-adjust-state-on-prop-change
        setCheckStockCode(true);
      } else {
        void playSound("low");
        void playHaptic("low");

        // eslint-disable-next-line react-you-might-not-need-an-effect-jscompat/no-chain-state-updates, react-you-might-not-need-an-effect-jscompat/no-adjust-state-on-prop-change
        setDisplayAlertUnrecognised(true);
      }
    }
    // eslint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
  }, [hasLoaded]);

  const renderResult = (result: StockLookupResponseRecord, idx: number) => {
    return (
      <YStack my="$3" gap="$3" key={idx}>
        {idx > 0 && <Separator mb="$3" />}
        <YStack flex={1}>
          <H6>{result.Barcode}</H6>
          <Paragraph color="$color">
            {query.isLoading ? "Loading..." : (result?.Full_Desc ?? "No matching product")}
          </Paragraph>
        </YStack>
        {!!result?.Deleted && (
          <XStack ai="center" theme="orange" bc="$backgroundStrong" br="$2" p="$2" gap="$2">
            <WarningIcon />
            <Paragraph color="$color">This product has been deleted</Paragraph>
          </XStack>
        )}
        {!!result?.Suspended && (
          <XStack ai="center" theme="orange" bc="$backgroundStrong" br="$2" p="$2" gap="$2">
            <WarningIcon />
            <Paragraph color="$color">This product has been suspended</Paragraph>
          </XStack>
        )}
        {!!result?.Discarded && (
          <XStack ai="center" theme="orange" bc="$backgroundStrong" br="$2" p="$2" gap="$2">
            <WarningIcon />
            <Paragraph color="$color">This product has been discarded</Paragraph>
          </XStack>
        )}
        <XStack flex={1} alignItems="center" justifyContent="space-between">
          <Paragraph color="$color">Price</Paragraph>
          <H4 color="$color">{result?.BandA_Sell ? formatPrice(result?.BandA_Sell) : ""}</H4>
        </XStack>
        <XStack flex={1} alignItems="center" justifyContent="space-between">
          <Paragraph color="$color">In stock</Paragraph>
          <H4 color="$color">{result?.Retail_Stock_Balance.toString() ?? ""}</H4>
        </XStack>
        <XStack flex={1} alignItems="center" justifyContent="space-between">
          <Paragraph color="$color">Stock code</Paragraph>
          <H4 color="$color">{result?.Stock_Code.toString() ?? ""}</H4>
        </XStack>
      </YStack>
    );
  };

  return (
    <YStack key={item.code} width="100%">
      <XStack gap="$2" jc="flex-end" ai="center">
        <XStack w={180}>
          <FilterSelect
            size="$3"
            value={sortBy}
            updateValue={(v) => setSortBy(v as "default")}
            options={[
              { id: "default", label: "Sort: Best Match" },
              { id: "price_asc", label: "Sort: Price (low-high)" },
              { id: "price_desc", label: "Sort: Price (high-low)" },
              { id: "desc_asc", label: "Sort: Description (A-Z)" },
              { id: "desc_desc", label: "Sort: Description (Z-A)" },
              { id: "code_asc", label: "Sort: Code (low-high)" },
              { id: "code_desc", label: "Sort: Code (high-low)" },
            ]}
          />
        </XStack>
        <YStack w={180}>
          <FilterSelect
            size="$3"
            value={stockFilter}
            updateValue={(v) => setStockFilter(v as StockFilter)}
            options={[
              { id: "none", label: "All stock" },
              { id: "excludeZero", label: "Non-zero stock only" },
              { id: "excludeNonPositive", label: "Positive stock only" },
            ]}
          />
        </YStack>
      </XStack>
      {query.isLoading && (
        <YStack flex={1}>
          <H6>{item.code}</H6>
          <Paragraph color="$color">Loading...</Paragraph>
        </YStack>
      )}

      {!query.isLoading && !queryResults.length && (
        <YStack flex={1}>
          <H6>{item.code}</H6>
          <Paragraph color="$color">No matching product</Paragraph>
        </YStack>
      )}

      {!query.isLoading && !!queryResults.length && (
        <>
          {!!query.data!.totalRecordsDisplay && (
            <XStack ai="center" theme="orange" bc="$backgroundStrong" br="$2" p="$2" gap="$2">
              <WarningIcon />
              <Paragraph color="$color">
                Showing {query.data!.records.length} out of {query.data!.totalRecordsDisplay}
              </Paragraph>
            </XStack>
          )}
          {unignoredResults.map(renderResult)}
          {ignoredResults.length > 0 && (
            <>
              <Separator mb="$3" />
              <Button
                size="$2"
                chromeless
                onPress={() => setShowIgnored(!showIgnored)}
                icon={showIgnored ? Eye : EyeOff}
              >
                {showIgnored ? "Hide Deleted/Suspended/Discarded" : "Show Deleted/Suspended/Discarded"}
              </Button>
              <YStack gap="$3">{showIgnored && ignoredResults.map(renderResult)}</YStack>
            </>
          )}
        </>
      )}

      <Alert
        display={displayAlertUnrecognised}
        title={item.searchDescription ? "No matching product found" : "Unrecognised barcode"}
        message={
          item.searchDescription
            ? `No products found matching "${item.searchDescription}"`
            : `Barcode ${item.code} has not been recognised`
        }
        buttons={[
          ...(Platform.OS === "web"
            ? []
            : [
                {
                  text: "Rescan",
                  onPress: () => {
                    setItem();
                    setIsCameraActive(true);
                  },
                },
              ]),

          {
            text: "Enter code manually",
            onPress: () => {
              setItem();
              setEditItem({ code: item.code });
              setShowEnterBarcode(true);
            },
          },
          {
            text: "Product search",
            onPress: () => {
              setItem();
              setEditItem({ searchDescription: item.searchDescription });
              setShowEnterDescription(true);
            },
          },
        ]}
        onClose={() => setDisplayAlertUnrecognised(false)}
        onCancel={() => setItem()}
      />
    </YStack>
  );
}

export function LookupProduct() {
  const [lookupItem, setLookupItem] = React.useState<LookupItem>();
  const [isCameraActive, setIsCameraActive] = React.useState(true);
  const [showEnterBarcode, setShowEnterBarcode] = React.useState(false);
  const [showEnterDescription, setShowEnterDescription] = React.useState(false);
  const [editLookupItem, setEditLookupItem] = React.useState<LookupItem>({
    code: "",
  });

  const hasCamera = useHasCamera();

  const [frameProcessor, barcodes] = useScanBarcodes(isCameraActive);

  React.useEffect(() => {
    const barcode = barcodes[0];
    // eslint-disable-next-line react-you-might-not-need-an-effect-jscompat/no-event-handler
    if (!isCameraActive || !barcode || lookupItem?.code) return;

    // eslint-disable-next-line react-you-might-not-need-an-effect-jscompat/no-derived-state
    setLookupItem({ code: String(barcode.displayValue || "") });
    // eslint-disable-next-line react-you-might-not-need-an-effect-jscompat/no-chain-state-updates
    setIsCameraActive(false);

    void playHaptic("mid");
    void playSound("mid");
    // eslint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
  }, [barcodes]);

  return (
    <PageContainer>
      <YStack animation="quick" height={isCameraActive ? 350 : 200} maxWidth={800} w="100%" als="center">
        <ManagedScanner
          frameProcessor={frameProcessor}
          isActive={isCameraActive}
          startScanning={() => {
            setLookupItem(undefined);
            setIsCameraActive(true);
          }}
        />
      </YStack>

      <PageScrollNarrow>
        {lookupItem ? (
          <LookupProductItem
            item={lookupItem}
            setItem={setLookupItem}
            setEditItem={(val) =>
              setEditLookupItem({
                code: String(val?.code ?? ""),
                searchDescription: val?.searchDescription,
              })
            }
            setIsCameraActive={setIsCameraActive}
            setShowEnterBarcode={setShowEnterBarcode}
            setShowEnterDescription={setShowEnterDescription}
            key={lookupItem.code}
          />
        ) : (
          <YStack gap="$3">
            <H4 mb="$3">Scan barcode or stock code to lookup product</H4>
            <Button
              onPress={() => {
                setShowEnterBarcode(true);
                setEditLookupItem({});
                setIsCameraActive(false);
              }}
            >
              Enter code manually
            </Button>
            <Button
              onPress={() => {
                setShowEnterDescription(true);
                setEditLookupItem({});
                setIsCameraActive(false);
              }}
            >
              Product search
            </Button>
          </YStack>
        )}

        <Alert
          display={showEnterBarcode}
          title="Enter code manually"
          inputs={[
            {
              defaultValue: editLookupItem.code?.toString(),
              placeholder: "123456",
              keyboardType: "numeric",
              autoFocus: true,
            },
          ]}
          buttons={[
            {
              text: "Done",
              onPress: (response) => {
                let inputZone = response?.inputs?.[0]?.value || "";
                inputZone = inputZone.replace(/\W/g, "");
                setLookupItem({ ...editLookupItem, code: inputZone });
              },
            },
          ]}
          onClose={() => {
            setShowEnterBarcode(false);
          }}
          onCancel={() => {
            setLookupItem(undefined);
          }}
        />
        <Alert
          display={showEnterDescription}
          title="Product search"
          inputs={[
            {
              defaultValue: editLookupItem.searchDescription?.toString(),
              placeholder: "e.g. watering can",
              autoFocus: true,
            },
          ]}
          buttons={[
            {
              text: "Done",
              onPress: (response) => {
                const inputZone = response?.inputs?.[0]?.value || "";
                setLookupItem({ ...editLookupItem, searchDescription: inputZone });
              },
            },
          ]}
          onClose={() => {
            setShowEnterDescription(false);
          }}
          onCancel={() => {
            setLookupItem(undefined);
          }}
        />
      </PageScrollNarrow>

      <YStack p="$3" gap="$3" maxWidth={800} w="100%" $gtSm={{ p: "$6" }} als="center">
        {lookupItem && !hasCamera && (
          <Button
            onPress={() => {
              let useProductLookup = false;
              if (lookupItem.searchDescription) {
                useProductLookup = true;
              }
              setLookupItem(undefined);
              setEditLookupItem({});
              if (useProductLookup) {
                setShowEnterDescription(true);
              } else {
                setShowEnterBarcode(true);
              }
            }}
          >
            Look up another product
          </Button>
        )}
        {lookupItem && hasCamera && (
          <Button
            onPress={() => {
              setLookupItem(undefined);
              setIsCameraActive(true);
            }}
          >
            Scan another item
          </Button>
        )}
        {!lookupItem && hasCamera && (
          <Button
            disabled={isCameraActive}
            onPress={() => {
              setIsCameraActive(true);
            }}
          >
            Scan
          </Button>
        )}
      </YStack>
    </PageContainer>
  );
}
